import { getActiveCompanyId } from "@/services/_headers.util";

function normalizeValue(value) {
  if (value === undefined) return "__undefined__";
  if (value === null) return null;

  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeValue(value[key]);
        return acc;
      }, {});
  }

  return value;
}

export function stableSerialize(value) {
  return JSON.stringify(normalizeValue(value));
}

export function buildScopedCacheKey(namespace, params = {}, extraScope = {}) {
  const companyId = getActiveCompanyId() || null;
  return `${namespace}:${stableSerialize({
    companyId,
    ...extraScope,
    params,
  })}`;
}

export function createScopedCache(namespace) {
  const cache = new Map();
  const inFlight = new Map();

  function isFresh(entry, ttlMs) {
    if (!entry) return false;
    if (ttlMs <= 0) return false;
    return Date.now() - entry.at < ttlMs;
  }

  async function get(params = {}, options = {}) {
    const {
      ttlMs = 60000,
      force = false,
      scope = {},
      loader,
      onHydrate,
    } = options;

    const key = buildScopedCacheKey(namespace, params, scope);

    if (!force && isFresh(cache.get(key), ttlMs)) {
      const cached = cache.get(key)?.value;
      if (onHydrate) onHydrate(cached, { fromCache: true, key });
      return cached;
    }

    if (!force && inFlight.has(key)) {
      return inFlight.get(key);
    }

    const request = Promise.resolve()
      .then(() => loader())
      .then((value) => {
        // Ignore stale responses after clear()/a newer request for the same key.
        if (inFlight.get(key) !== request) {
          return value;
        }
        cache.set(key, { value, at: Date.now() });
        if (onHydrate) onHydrate(value, { fromCache: false, key });
        return value;
      })
      .finally(() => {
        if (inFlight.get(key) === request) {
          inFlight.delete(key);
        }
      });

    inFlight.set(key, request);
    return request;
  }

  function invalidate(params = null, scope = {}) {
    if (params === null) {
      const companyId = scope.companyId ?? getActiveCompanyId() ?? null;
      const needle = stableSerialize({ companyId, ...scope }).replace(
        /^{|}$/g,
        "",
      );

      [...cache.keys()].forEach((key) => {
        if (
          key.startsWith(`${namespace}:`) &&
          (needle === "" || key.includes(needle))
        ) {
          cache.delete(key);
        }
      });
      return;
    }

    cache.delete(buildScopedCacheKey(namespace, params, scope));
  }

  function clear() {
    cache.clear();
    inFlight.clear();
  }

  return {
    get,
    invalidate,
    clear,
  };
}
